import type { RoundCountSummary } from "@cloudnest/horror-race-engine";
import { SUMMARY_CHARACTER_ORDER } from "@cloudnest/horror-race-engine";

interface CoinMemoryRowProps {
  readonly summary: RoundCountSummary;
}

const LBL_BASE: React.CSSProperties = {
  textAlign: "left",
  paddingLeft: "10px",
  fontWeight: 700,
  color: "var(--cn-horror-text)",
  whiteSpace: "nowrap",
};
const CELL_BASE: React.CSSProperties = {
  padding: "6px 4px",
  textAlign: "center",
  fontFamily: "ui-monospace, monospace",
  fontWeight: 600,
};
const ZERO_STYLE: React.CSSProperties = {
  color: "var(--cn-horror-text-muted)",
  opacity: 0.4,
};
const UNK_STYLE: React.CSSProperties = {
  color: "var(--cn-horror-error)",
  fontFamily: "ui-monospace, monospace",
  fontWeight: 700,
};
const UNK_ZERO_STYLE: React.CSSProperties = {
  color: "var(--cn-horror-text-muted)",
  opacity: 0.35,
  fontFamily: "ui-monospace, monospace",
  fontWeight: 500,
};

/**
 * M1 — single round row for the coin-memory sheet. 7 cells: round label · 5
 * character counts · `?` (unknown / forfeit) count.
 *
 * Visual policy (tone-down v6):
 *   - complete (no forfeit, all turns drawn) → thin gold left-bar on lbl cell
 *   - live (current round, mid-progress)    → italic dim label
 *   - regular                                → no extra emphasis
 *
 * No row halos, no `★` prefix here — the "exact" summary row owns the only
 * loud emphasis at the top of the table.
 */
export function CoinMemoryRow({ summary }: CoinMemoryRowProps) {
  const lblStyle: React.CSSProperties = summary.isComplete
    ? {
        ...LBL_BASE,
        boxShadow: "inset 3px 0 0 var(--cn-horror-finished-flash)",
      }
    : summary.isLive
      ? {
          ...LBL_BASE,
          fontStyle: "italic",
          color: "var(--cn-horror-text-muted)",
        }
      : LBL_BASE;

  return (
    <tr
      data-testid="round-count-row"
      data-round-number={summary.roundNumber}
      data-complete={summary.isComplete ? "true" : "false"}
      data-live={summary.isLive ? "true" : "false"}
    >
      <td style={lblStyle}>R{summary.roundNumber}</td>
      {SUMMARY_CHARACTER_ORDER.map((char) => {
        const n = summary.counts[char];
        return (
          <td
            key={char}
            style={n === 0 ? { ...CELL_BASE, ...ZERO_STYLE } : CELL_BASE}
          >
            {n}
          </td>
        );
      })}
      <td style={summary.unknownCount === 0 ? UNK_ZERO_STYLE : UNK_STYLE}>
        {summary.unknownCount}
      </td>
    </tr>
  );
}
